"use client";
import { getCartClient } from "@/api/saleor/services/checkoutClient";
import {
  createAnonymousUserObject,
  isCncOrder,
  setCurrentPageUrlToSession,
  triggerCartItemEvent,
  triggerCustomViewEvent,
} from "@/apiUtils/commonUtils";
import { pageTypes } from "@/apiUtils/constants";
import { Checkout, Order } from "@/common/lib/types";
import { getCncPersonalDetails } from "@/hooks/useCncPersonalDetails";
import useSearchParams from "@/hooks/useSearchParams";
import { useGlobalStore } from "@/store/store";
import { getClientCookie } from "@/utils/common";
import { useParams } from "next/navigation";
import { FC, useEffect, useMemo, useRef } from "react";

interface HeaderEventsProps {
  pageType: string;
  pageCategory?: string;
  checkout?: Checkout | Order;
}

const HeaderEvents: FC<HeaderEventsProps> = ({ pageType, pageCategory, checkout }) => {
  const onlyOnceRef = useRef<boolean>(true);
  const isEventTriggered = useRef<boolean>(false);
  const transactional = !["CHECKOUT", "CHECKOUT_PAYMENT"].includes(pageType);
  const { searchParams } = useSearchParams();
  const { locale: localeParam } = useParams();
  const { user, setUser: updateAnonymousUser } = useGlobalStore();

  const locale = useMemo(() => {
    return localeParam
      ? (localeParam as string)
      : (getClientCookie("NEXT_LOCALE")?.split("-")?.[1] ?? "en");
  }, []);

  function checkIfAllFieldsAreFalsy(obj: Record<string, any>) {
    for (const val of Object.values(obj)) {
      if (val) return false;
    }

    return true;
  }

  useEffect(() => {
    // locale = getClientCookie("NEXT_LOCALE")?.split("-")?.[1] ?? "en";

    const postGaEvents = async () => {
      let cart = null;
      let actualOrder = null;
      let cncPersonalDetails = null;

      const gaUser = user?.email
        ? user
        : createAnonymousUserObject(actualOrder, cncPersonalDetails);
      // Updating to context for handle in pdp variant switching
      if (!user?.email && gaUser) {
        if (!checkIfAllFieldsAreFalsy(gaUser)) updateAnonymousUser(gaUser);
      }
      let pageCategoryInParams = "";
      if (typeof window !== "undefined" && pageType === pageTypes.PDP) {
        pageCategoryInParams = window?.location?.search?.split(" - ")?.slice(1)?.join(" - ");
      }
      const pageCategoryIn = pageCategoryInParams || pageCategory || "";
      await triggerCustomViewEvent(gaUser, locale, pageType, pageCategoryIn);

      if (transactional && pageType === pageTypes.CART && checkout) {
        // Cart page passes the SSR checkout down, so skip the client refetch entirely.
        cart = { checkout: checkout as Checkout, errors: [] };
      } else if (transactional && !checkout) {
        cart = await getCartClient();
      }

      if (cart) {
        actualOrder = cart?.checkout;
        await triggerCartItemEvent(cart, locale);
        cncPersonalDetails = isCncOrder(cart?.checkout)
          ? getCncPersonalDetails(cart?.checkout?.billingAddress)
          : null;
      } else if (transactional && checkout) {
        // For Checkout pages and order confirmation page
        actualOrder = checkout;
        cncPersonalDetails = isCncOrder(checkout)
          ? getCncPersonalDetails(checkout?.billingAddress)
          : null;
        if (pageType === pageTypes.ORDER_CONFIRMATION) {
          await triggerCartItemEvent(null, locale);
        }
      }

      isEventTriggered.current = true;
    };

    if (onlyOnceRef?.current) {
      onlyOnceRef.current = false;
      postGaEvents();
    }
  }, [
    checkout,
    locale,
    pageCategory,
    pageType,
    transactional,
    updateAnonymousUser,
    user?.magrabi_id,
  ]);

  useEffect(() => {
    if (typeof window !== "undefined" && isEventTriggered?.current) {
      setCurrentPageUrlToSession(window?.location?.href);
    }
  }, [searchParams]);

  return <></>;
};

export default HeaderEvents;
